import React, {useCallback, useEffect, useRef} from 'react'
import {ScrollView, StyleSheet, View} from 'react-native'
import {
  type $Typed,
  type AppBskyEmbedRecord,
  AppBskyRichtextFacet,
  RichText,
} from '@atproto/api'

import {shortenLinks, stripInvalidMentions} from '#/lib/strings/rich-text-manip'
import {
  convertBskyAppUrlIfNeeded,
  isBskyPostUrl,
} from '#/lib/strings/url-helpers'
import {logger} from '#/logger'
import {isConvoActive, useConvo, useConvoActive} from '#/state/messages/convo'
import {type ConvoItem, ConvoStatus} from '#/state/messages/convo/types'
import {useGetPost} from '#/state/queries/post'
import {useAgent} from '#/state/session'
import {atoms as a, useTheme} from '#/alf'
import {FloatingMessageInput} from '#/components/chat-bubble/FloatingMessageInput.web'
import {ChatEmptyPill} from '#/components/dms/ChatEmptyPill'
import {MessageItem} from '#/components/dms/MessageItem'
import {Loader} from '#/components/Loader'
import {Text} from '#/components/Typography'

function renderConvoItem(item: ConvoItem) {
  if (item.type === 'message' || item.type === 'pending-message') {
    return <MessageItem item={item} />
  } else if (item.type === 'deleted-message') {
    return null
  } else if (item.type === 'error') {
    return (
      <View style={{padding: 8, alignItems: 'center'}}>
        <Text style={[a.text_xs, {color: '#ef4444'}]}>
          Failed to load messages
        </Text>
      </View>
    )
  }
  return null
}

export function FloatingMessagesList() {
  const t = useTheme()
  const convoState = useConvo()

  if (convoState.status === ConvoStatus.Error) {
    return (
      <View
        style={[
          styles.container,
          t.atoms.bg,
          {justifyContent: 'center', alignItems: 'center'},
        ]}>
        <Text style={[a.text_sm, {color: t.palette.contrast_500}]}>
          Couldn't load conversation
        </Text>
      </View>
    )
  }

  if (!isConvoActive(convoState)) {
    return (
      <View
        style={[
          styles.container,
          t.atoms.bg,
          {justifyContent: 'center', alignItems: 'center'},
        ]}>
        <Loader size="lg" />
      </View>
    )
  }

  return <FloatingMessagesListInner />
}

function FloatingMessagesListInner() {
  const t = useTheme()
  const convoState = useConvoActive()
  const agent = useAgent()
  const getPost = useGetPost()
  const scrollViewRef = useRef<ScrollView>(null)
  const prevItemCount = useRef(0)

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (convoState.items.length > prevItemCount.current) {
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({animated: true})
      }, 50)
    }
    prevItemCount.current = convoState.items.length
  }, [convoState.items.length])

  // Scroll to bottom on mount
  useEffect(() => {
    setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({animated: false})
    }, 100)
  }, [])

  const onSendMessage = useCallback(
    async (text: string) => {
      let rt = new RichText({text: text.trimEnd()}, {cleanNewlines: true})
      rt.detectFacetsWithoutResolution()

      let embed: $Typed<AppBskyEmbedRecord.Main> | undefined

      // Check for post embeds in the text
      if (rt.facets) {
        for (const facet of rt.facets) {
          for (const feature of facet.features) {
            if (AppBskyRichtextFacet.isLink(feature) && isBskyPostUrl(feature.uri)) {
              try {
                const url = convertBskyAppUrlIfNeeded(feature.uri)
                const post = await getPost({uri: url})
                if (post) {
                  embed = {
                    $type: 'app.bsky.embed.record',
                    record: {uri: post.uri, cid: post.cid},
                  }
                }
              } catch (error) {
                logger.error('Failed to get post as quote for DM', {error})
              }
              break
            }
          }
        }
      }

      await rt.detectFacets(agent)
      rt = shortenLinks(rt)
      rt = stripInvalidMentions(rt)

      convoState.sendMessage({
        text: rt.text,
        facets: rt.facets,
        embed,
      })
    },
    [agent, convoState, getPost],
  )

  const isReady = isConvoActive(convoState)
  const isNewChat =
    isReady && !convoState.isFetchingHistory && convoState.items.length === 0

  return (
    <View style={[styles.container, t.atoms.bg]}>
      <ScrollView
        ref={scrollViewRef}
        style={styles.messagesList}
        contentContainerStyle={styles.messagesContent}
        showsVerticalScrollIndicator={true}
        onScroll={e => {
          // Load more history when scrolled to top
          if (
            e.nativeEvent.contentOffset.y < 50 &&
            isReady &&
            !convoState.isFetchingHistory
          ) {
            convoState.fetchMessageHistory()
          }
        }}
        scrollEventThrottle={100}>
        {convoState.isFetchingHistory && (
          <View style={styles.loader}>
            <Loader size="md" />
          </View>
        )}
        {/* Messages are in chronological order (oldest first) - reverse the items array */}
        {[...convoState.items].reverse().map(item => (
          <View key={item.key}>{renderConvoItem(item)}</View>
        ))}
      </ScrollView>

      {isNewChat && <ChatEmptyPill />}

      {convoState.status !== ConvoStatus.Disabled && (
        <FloatingMessageInput onSendMessage={onSendMessage} />
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    overflow: 'hidden',
  },
  messagesList: {
    flex: 1,
  },
  messagesContent: {
    paddingVertical: 8,
  },
  loader: {
    padding: 12,
    alignItems: 'center',
  },
})
